import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { ArticleType, ARTICLE_TYPE_LABELS } from '../../types/knowledgeBase';
import './TemplateSelector.css';

interface TemplateOption {
  type: ArticleType;
  title: string;
  description: string;
  icon: React.ReactNode;
  features: readonly string[];
}

const TEMPLATE_OPTIONS: readonly TemplateOption[] = [
  {
    type: ArticleType.HOW_TO,
    title: ARTICLE_TYPE_LABELS[ArticleType.HOW_TO],
    description: 'Step-by-step instructions for completing a task or process.',
    icon: (
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M8 10h16M8 16h12M8 22h8"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx="5" cy="10" r="2" fill="currentColor" />
        <circle cx="5" cy="16" r="2" fill="currentColor" />
        <circle cx="5" cy="22" r="2" fill="currentColor" />
      </svg>
    ),
    features: ['Numbered steps', 'Screenshots per step', 'Sequential workflow'],
  },
  {
    type: ArticleType.KNOWN_ERROR,
    title: ARTICLE_TYPE_LABELS[ArticleType.KNOWN_ERROR],
    description: 'Document known issues with symptoms, causes, and solutions.',
    icon: (
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M16 6L4 26h24L16 6z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M16 14v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="16" cy="22" r="1" fill="currentColor" />
      </svg>
    ),
    features: ['Symptoms & root cause', 'Workaround & resolution', 'Related articles linking'],
  },
  {
    type: ArticleType.SOP_RUNBOOK,
    title: ARTICLE_TYPE_LABELS[ArticleType.SOP_RUNBOOK],
    description: 'Standard operating procedures with prerequisites and verification steps.',
    icon: (
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <rect x="6" y="4" width="20" height="24" rx="2" stroke="currentColor" strokeWidth="2" />
        <path
          d="M10 10h12M10 15h12M10 20h8"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M10 25l2 2 4-4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    features: ['Prerequisites checklist', 'Verification steps', 'Emergency contacts'],
  },
] as const;

export function TemplateSelector() {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const handleSelectTemplate = useCallback(
    (type: ArticleType) => {
      navigate(`/knowledge-base/new/${type}`);
    },
    [navigate]
  );

  const handleBack = useCallback(() => {
    navigate('/knowledge-base/articles');
  }, [navigate]);

  return (
    <Box className="template-selector" data-theme={theme}>
      <Box className="template-selector__container">
        <Box className="template-selector__header">
          <button className="template-selector__back" onClick={handleBack} type="button">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 15l-5-5 5-5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back to Knowledge Base
          </button>
        </Box>

        <Box className="template-selector__content">
          <Typography variant="h5" component="h1" className="template-selector__title">
            Choose a Template
          </Typography>
          <Typography variant="body1" className="template-selector__subtitle">
            Select the type of article you want to create. Each template has specialized fields
            to help structure your content.
          </Typography>

          <Box className="template-selector__options">
            {TEMPLATE_OPTIONS.map((option) => (
              <button
                key={option.type}
                className="template-selector__option"
                onClick={() => handleSelectTemplate(option.type)}
                type="button"
              >
                <Box className="template-selector__option-icon">{option.icon}</Box>
                <Box className="template-selector__option-content">
                  <Typography variant="h6" className="template-selector__option-title">
                    {option.title}
                  </Typography>
                  <Typography variant="body2" className="template-selector__option-description">
                    {option.description}
                  </Typography>
                  <Box className="template-selector__option-features">
                    {option.features.map((feature) => (
                      <span key={feature} className="template-selector__feature">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2.5 6l2.5 2.5 5-5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {feature}
                      </span>
                    ))}
                  </Box>
                </Box>
                <Box className="template-selector__option-arrow">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 5l5 5-5 5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
              </button>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
